import React, { useState, useEffect } from 'react';
import { MessageSquare, Check, X, Clock, User, Eye } from 'lucide-react';
import api from '../services/api';

function PendingExtraNotesManager({ onNotesUpdate }) {
  const [pendingNotes, setPendingNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedNote, setSelectedNote] = useState(null);

  useEffect(() => {
    loadPendingNotes();
  }, []);

  const loadPendingNotes = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.getPendingExtraNotes();
      setPendingNotes(response.pendingNotes || []);
    } catch (err) {
      console.error('Error loading pending extra notes:', err);
      setError('Failed to load pending extra notes');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveNote = async noteId => {
    setLoading(true);
    setError('');
    try {
      await api.approveExtraNote(noteId);
      await loadPendingNotes();
      if (onNotesUpdate) onNotesUpdate();
    } catch (err) {
      console.error('Error approving extra note:', err);
      setError('Failed to approve extra note');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectNote = async noteId => {
    if (!window.confirm('Are you sure you want to reject and delete this note?')) return;

    setLoading(true);
    setError('');
    try {
      await api.rejectExtraNote(noteId);
      await loadPendingNotes();
      if (onNotesUpdate) onNotesUpdate();
    } catch (err) {
      console.error('Error rejecting extra note:', err);
      setError('Failed to reject extra note');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = timestamp => {
    return new Date(timestamp).toLocaleString();
  };

  const formatIncidentType = type => {
    if (!type) return 'Unknown';
    return type
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading && pendingNotes.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <MessageSquare className="w-5 h-5 text-yellow-600" />
          <h3 className="text-lg font-semibold text-gray-900">Pending Extra Notes</h3>
          <span className="text-sm text-gray-500">({pendingNotes.length})</span>
        </div>
        <button
          onClick={loadPendingNotes}
          disabled={loading}
          className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Pending Notes List */}
      <div className="space-y-3">
        {pendingNotes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No pending extra notes</p>
            <p className="text-sm">All extra notes have been moderated</p>
          </div>
        ) : (
          pendingNotes.map(note => (
            <div key={note.id} className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
              {/* Note Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      {note.user?.name || note.userEmail}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4 text-yellow-600" />
                    <span className="text-xs text-yellow-600 font-medium">Pending Approval</span>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setSelectedNote(selectedNote?.id === note.id ? null : note)}
                    className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                    title="View details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleApproveNote(note.id)}
                    disabled={loading}
                    className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                    title="Approve note"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleRejectNote(note.id)}
                    disabled={loading}
                    className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                    title="Reject note"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Note Content */}
              <div className="text-gray-800 whitespace-pre-wrap mb-3">{note.noteText}</div>

              {/* Report Information */}
              {note.Report && (
                <div className="bg-white border border-gray-200 rounded p-3 mb-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Related Report</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="font-medium text-gray-600">ID:</span> {note.Report.id}
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Type:</span>{' '}
                      {formatIncidentType(note.Report.incidentType)}
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Reporter:</span>{' '}
                      {note.Report.reporterName}
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Location:</span>{' '}
                      {note.Report.location}
                    </div>
                    <div className="md:col-span-2">
                      <span className="font-medium text-gray-600">Description:</span>{' '}
                      {note.Report.description}
                    </div>
                  </div>
                </div>
              )}

              {/* Note Footer */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <div className="text-xs text-gray-500">Added: {formatTime(note.createdAt)}</div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleApproveNote(note.id)}
                    disabled={loading}
                    className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center space-x-1"
                  >
                    <Check className="w-3 h-3" />
                    <span>Approve</span>
                  </button>
                  <button
                    onClick={() => handleRejectNote(note.id)}
                    disabled={loading}
                    className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center space-x-1"
                  >
                    <X className="w-3 h-3" />
                    <span>Reject</span>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default PendingExtraNotesManager;
